/**
 * validateRequiredFields - Validador centralizado de campos obrigatórios
 *
 * Este é o ÚNICO lugar onde validação de required deve acontecer.
 * Usado por: GenericDrawer, GenericInlineEntityManager, SimpleFormModal, *Details
 */

export type FieldLabel = string | ((data: Record<string, unknown>) => string);

export type FieldConfig = {
  field: string;
  label?: FieldLabel;
  required?: boolean;
  // Mensagem customizada quando o campo está vazio. Se omitido, usa `${label} é obrigatório`.
  // Aceita função para mensagens contextuais (ex.: concordância de gênero PF/PJ).
  requiredMessage?: FieldLabel;
};

export type ValidateOptions = {
  onError?: (message: string) => void;
  collectAll?: boolean;
};

export type ValidateResult = {
  isValid: boolean;
  errors: Record<string, string>;
  firstError: string | null;
};

function resolveText(
  source: FieldLabel | undefined,
  data: Record<string, unknown>
): string | undefined {
  if (source == null) return undefined;
  return typeof source === 'function' ? source(data) : source;
}

export function validateRequiredFields(
  data: Record<string, unknown>,
  fields: readonly FieldConfig[],
  options: ValidateOptions = {}
): ValidateResult {
  const { onError, collectAll = false } = options;

  // Filtrar apenas campos com required: true
  const requiredFields = fields.filter(f => f.required === true);

  const errors: Record<string, string> = {};
  let firstError: string | null = null;

  for (const field of requiredFields) {
    const value = data[field.field];
    const isEmpty = isEmptyValue(value);

    if (isEmpty) {
      const customMessage = resolveText(field.requiredMessage, data);
      const labelText = resolveText(field.label, data) || field.field;
      const errorMessage = customMessage || `${labelText} é obrigatório`;

      errors[field.field] = errorMessage;

      if (!firstError) {
        firstError = errorMessage;
      }

      // Se não estamos coletando todos, retornar no primeiro erro
      if (!collectAll) {
        if (onError) onError(errorMessage);
        return { isValid: false, errors, firstError };
      }
    }
  }

  const isValid = Object.keys(errors).length === 0;

  // Se coletou todos os erros e tem erros, chamar onError com o primeiro
  if (!isValid && onError && firstError) {
    onError(firstError);
  }

  return { isValid, errors, firstError };
}

/**
 * isEmptyValue - Verifica se um valor é considerado "vazio"
 */
export function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

/**
 * getRequiredFieldNames - Retorna lista de campos obrigatórios
 */
export function getRequiredFieldNames(fields: readonly FieldConfig[]): string[] {
  return fields
    .filter(f => f.required === true)
    .map(f => f.field);
}

/**
 * isFieldRequired - Verifica se campo deve ter asterisco
 */
export function isFieldRequired(fieldName: string, fields: readonly FieldConfig[]): boolean {
  const field = fields.find(f => f.field === fieldName);
  return field?.required === true;
}

export default validateRequiredFields;
